import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Droplets, ShoppingCart, User, Moon, Sun, Menu, X, MessageCircle, FlaskConical, Store, LogOut, CalendarDays, Settings, Briefcase, Waves, Camera, Beaker, ChevronDown, Coins, Gift, CloudSun, Bell, BookOpen, Calculator } from 'lucide-react';
import { Button } from '../ui/button';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import LanguageSelector from '../LanguageSelector';
import GlobalSearch from '../GlobalSearch';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';

export function Navbar() {
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { theme, toggleTheme } = useTheme();
    const { user, logout } = useAuth();
    const { itemCount } = useCart();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const navLinks = [
        { path: '/weather', label: t('nav.weather'), icon: CloudSun },
        { path: '/chat', label: t('nav.assistant'), icon: MessageCircle },
        { path: '/shop', label: t('nav.shop'), icon: Store },
        { path: '/blog', label: 'Conseils', icon: BookOpen },
        { path: '/appointment', label: t('nav.appointments'), icon: CalendarDays },
    ];

    const analysisLinks = [
        { path: '/analysis', label: t('navbar.manualAnalysis'), icon: FlaskConical },
        { path: '/analysis/photo', label: t('navbar.photoAnalysis'), icon: Camera },
        { path: '/analysis/spintouch', label: t('navbar.spintouch'), icon: Beaker },
        { path: '/calculateur', label: 'Calculateur dosage', icon: Calculator },
    ];

    const isActive = (path) => location.pathname === path;

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    return (
        <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50" data-testid="navbar">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
                        <div className="relative">
                            <Droplets className="h-8 w-8 text-primary transition-transform duration-300 group-hover:scale-110" />
                            <div className="absolute inset-0 bg-primary/20 rounded-full blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        </div>
                        <span className="font-heading font-bold text-xl">O.C.A Piscine</span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center gap-1">
                        {/* Analysis Dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="ghost"
                                    className={`nav-link flex items-center gap-2 px-4 py-2 rounded-full transition-colors duration-200 ${
                                        location.pathname.startsWith('/analysis') 
                                            ? 'text-primary bg-primary/10' 
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    <FlaskConical className="h-4 w-4" />
                                    <span className="font-medium">{t('navbar.analysis')}</span>
                                    <ChevronDown className="h-3 w-3" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-56">
                                {analysisLinks.map(({ path, label, icon: Icon }) => (
                                    <DropdownMenuItem key={path} onClick={() => navigate(path)}>
                                        <Icon className="h-4 w-4 mr-2" />
                                        {label}
                                    </DropdownMenuItem>
                                ))}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate('/analysis/history')}>
                                    <FlaskConical className="h-4 w-4 mr-2" />
                                    {t('navbar.history')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Other nav links */}
                        {navLinks.map(({ path, label, icon: Icon }) => (
                            <Link
                                key={path}
                                to={path}
                                className={`nav-link flex items-center gap-2 px-4 py-2 rounded-full transition-colors duration-200 ${
                                    isActive(path) 
                                        ? 'text-primary bg-primary/10' 
                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                }`}
                                data-testid={`nav-${path.slice(1)}`}
                            >
                                <Icon className="h-4 w-4" />
                                <span className="font-medium">{label}</span>
                            </Link>
                        ))}
                    </div>

                    {/* Right side actions */}
                    <div className="flex items-center gap-2">
                        {/* Global Search */}
                        <div className="hidden sm:block">
                            <GlobalSearch />
                        </div>

                        {/* Loyalty Points Widget - only shown when logged in */}
                        {user && (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        className="hidden sm:flex items-center gap-2 rounded-full px-3 hover:bg-amber-500/10"
                                        data-testid="loyalty-widget"
                                    >
                                        <div className="relative">
                                            <Coins className="h-5 w-5 text-amber-500" />
                                            <div className="absolute -top-1 -right-1 h-3 w-3 bg-amber-500 rounded-full animate-pulse" />
                                        </div>
                                        <span className="font-bold text-amber-500">
                                            {user.loyalty_points || 0}
                                        </span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-72">
                                    <div className="p-4 text-center bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-lg m-2">
                                        <Gift className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                                        <p className="font-heading font-bold text-2xl text-amber-500">
                                            {user.loyalty_points || 0} points
                                        </p>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            = {user.loyalty_points || 0}€ de réduction
                                        </p>
                                    </div>
                                    <div className="px-4 py-2 text-xs text-muted-foreground text-center">
                                        <p><strong>1 point = 1€</strong></p>
                                        <p>Gagnez 1 point tous les 10€ d&apos;achat</p>
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate('/loyalty')} data-testid="loyalty-history-link">
                                        <Coins className="h-4 w-4 mr-2" />
                                        Historique des points
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/shop')}>
                                        <Store className="h-4 w-4 mr-2" />
                                        Gagner plus de points
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        )}

                        {/* Theme toggle */}
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={toggleTheme}
                            className="rounded-full"
                            data-testid="theme-toggle"
                        >
                            {theme === 'dark' ? (
                                <Sun className="h-5 w-5" />
                            ) : (
                                <Moon className="h-5 w-5" />
                            )}
                        </Button>

                        {/* Language Selector */}
                        <LanguageSelector />

                        {/* Cart */}
                        <Link to="/cart" className="relative" data-testid="cart-link">
                            <Button variant="ghost" size="icon" className="rounded-full">
                                <ShoppingCart className="h-5 w-5" />
                                {itemCount > 0 && (
                                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center">
                                        {itemCount}
                                    </span>
                                )}
                            </Button>
                        </Link>

                        {/* User menu */}
                        {user ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu">
                                        {user.picture ? (
                                            <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
                                        ) : (
                                            <User className="h-5 w-5" />
                                        )}
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <div className="px-3 py-2">
                                        <p className="font-medium">{user.name}</p>
                                        <p className="text-sm text-muted-foreground">{user.email}</p>
                                        {user.loyalty_points > 0 && (
                                            <div className="flex items-center gap-1 mt-1">
                                                <Coins className="h-3 w-3 text-amber-500" />
                                                <span className="text-xs font-bold text-amber-500">{user.loyalty_points} points</span>
                                            </div>
                                        )}
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate('/loyalty')} data-testid="loyalty-link">
                                        <Coins className="h-4 w-4 mr-2 text-amber-500" />
                                        {t('navbar.myLoyaltyPoints')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/orders')} data-testid="orders-link">
                                        {t('navbar.myOrders')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/my-pool')} data-testid="my-pool-link">
                                        <Waves className="h-4 w-4 mr-2" />
                                        {t('navbar.myPool')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/my-analyses')} data-testid="my-analyses-link">
                                        <FlaskConical className="h-4 w-4 mr-2" />
                                        {t('navbar.myAnalyses')}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/notifications')} data-testid="notifications-link">
                                        <Bell className="h-4 w-4 mr-2" />
                                        Notifications
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate('/pro')} data-testid="pro-link">
                                        <Briefcase className="h-4 w-4 mr-2" />
                                        {t('navbar.proArea')}
                                    </DropdownMenuItem>
                                    {user?.email === 'admin@ocapiscine.fr' && (
                                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-link">
                                            <Settings className="h-4 w-4 mr-2" />
                                            {t('navbar.administration')}
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="logout-btn">
                                        <LogOut className="h-4 w-4 mr-2" />
                                        {t('navbar.logout')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Link to="/auth" data-testid="login-link">
                                <Button className="rounded-full">{t('nav.login')}</Button>
                            </Link>
                        )}

                        {/* Mobile menu button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="md:hidden rounded-full"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            data-testid="mobile-menu-toggle"
                        >
                            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            {mobileMenuOpen && (
                <div className="md:hidden glass border-t border-border/50 animate-slide-up">
                    <div className="px-4 py-4 space-y-2">
                        {/* Analysis section */}
                        <div className="space-y-1">
                            <div className="px-4 py-2 text-sm font-semibold text-muted-foreground">{t('navbar.analysis')}</div>
                            {analysisLinks.map(({ path, label, icon: Icon }) => (
                                <Link
                                    key={path}
                                    to={path}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                                        isActive(path) 
                                            ? 'text-primary bg-primary/10' 
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                    onClick={() => setMobileMenuOpen(false)}
                                >
                                    <Icon className="h-5 w-5" />
                                    <span className="font-medium">{label}</span>
                                </Link>
                            ))}
                            <Link
                                to="/analysis/history"
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                                    isActive('/analysis/history') 
                                        ? 'text-primary bg-primary/10' 
                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                }`}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <FlaskConical className="h-5 w-5" />
                                <span className="font-medium">{t('navbar.history')}</span>
                            </Link>
                        </div>
                        
                        {/* Separator */}
                        <div className="border-t border-border/50 my-2"></div>
                        
                        {/* Other nav links */}
                        {navLinks.map(({ path, label, icon: Icon }) => (
                            <Link
                                key={path}
                                to={path}
                                className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors duration-200 ${
                                    isActive(path) 
                                        ? 'text-primary bg-primary/10' 
                                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                }`}
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <Icon className="h-5 w-5" />
                                <span className="font-medium">{label}</span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </nav>
    );
}
